import { tryParseJSON } from '@/utils/tryParseJSON';
import { logger } from '@/logger';
import LogRocket from 'logrocket';
import ArgsType = jest.ArgsType;

export interface ILogrocketConfig {
  id: string;
}

const SENSITIVE_FIELDS = ['password', 'oldPassword', 'newPassword', 'confirmPassword'];

/**
 * The capture options for logrocket. This is in their API but the interface is not exported. Apparently
 * the hundreds of thousands of pounds we give them isn't enough to hire a dev who knows typescript :(
 */
type ICaptureOptions = ArgsType<(typeof LogRocket)['captureMessage']>[1];

export class LogrocketService {
  private logrocket: typeof LogRocket;
  public readonly config: ILogrocketConfig;

  constructor(config: ILogrocketConfig) {
    this.config = config;
    this.logrocket = LogRocket;
  }

  public init() {
    if (!this.config.id) {
      return; // Just don't use logrocket if we can't use logrocket
    }
    this.logrocket.init(this.config.id, {
      release: process.env.NEXT_PUBLIC_RELEASE_ID,
      network: {
        requestSanitizer(request) {
          if (request.body) {
            const parsedBody = tryParseJSON(request.body);

            if (parsedBody) {
              SENSITIVE_FIELDS.forEach(field => {
                if (parsedBody[field]) {
                  parsedBody[field] = '****';
                }
              });

              if (parsedBody.extras) {
                SENSITIVE_FIELDS.forEach(field => {
                  if (parsedBody.extras[field]) {
                    parsedBody.extras[field] = '****';
                  }
                });
              }

              request.body = JSON.stringify(parsedBody);
            }
          }

          return request;
        },
      },
    });
  }

  public identify(id: string, name: string, email: string, homeLocation: string) {
    this.logrocket.identify(String(id), {
      name,
      email,
      homeLocation,
    });
  }

  public logMessage(message: string, data?: ICaptureOptions) {
    this.logrocket.captureMessage(message, data);
  }
  public logException(exception: Error, data?: ICaptureOptions) {
    this.logrocket.captureException(exception, data);
    logger.error({ err: exception, data });
  }

  static defaultConfig() {
    return {
      id: process.env.NEXT_PUBLIC_LOGROCKET_ID as string,
    };
  }
}

const logrocketService = new LogrocketService(LogrocketService.defaultConfig());
logrocketService.init();
export { logrocketService };
